import propTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTheme } from 'styled-components';
import { getNestedErrorMessage } from '@/utils/utils';
import ValidationErrorMessage from '@/components/error/validation-error-component';
import Styled from './input-style';

/**
 * Register options for React Hook Form.
 *
 * @typedef {object} RegisterOptions
 * @property {React.Ref<any>} [ref] - React element ref.
 * @property {boolean | { value: boolean, message: string }} [required] - A Boolean which, if true, indicates that the input must have a value before the form can be submitted. You can assign a string to return an error message in the errors object. Note: This config aligns with web constrained API for required input validation, for object or array type of input use validate function instead.
 * @property {{ value: number, message: string }} [maxLength] - The maximum length of the input value, and an optional `message` (string) for the error message.
 * @property {{ value: number, message: string }} [minLength] - The minimum length of the input value, and an optional `message` (string) for the error message.
 * @property {{ value: number, message: string }} [max] - The maximum allowed numeric value, and an optional `message` (string) for the error message.
 * @property {{ value: number, message: string }} [min] - The minimum allowed numeric value, and an optional `message` (string) for the error message.
 * @property {{ value: RegExp, message: string }} [pattern] - A regular expression to match the input value, and an optional `message` (string) for the error message.
 * @property {(value: any) => boolean | string | Promise<boolean | string>} [validate] - A custom validation function or a function returning a promise resolving to a boolean or string. If a string is returned, it will be used as the error message.
 * @property {boolean} [valueAsNumber] - Whether to treat the value as a number (useful for numeric inputs).
 * @property {boolean} [valueAsDate] - Whether to treat the value as a date.
 * @property {(value: any) => any} [setValueAs] - A function to transform the value before setting it in the form state.
 * @property {boolean} [disabled] - Whether the input is disabled (default: false).
 * @property {(event: React.ChangeEvent<HTMLInputElement>) => void} [onChange] - A function to handle input changes.
 * @property {(event: React.FocusEvent<HTMLInputElement>) => void} [onBlur] - A function to handle input blur events.
 * @property {any} [value] - The initial value of the input.
 * @property {boolean} [shouldUnregister] - Whether to unregister the input on unmount.
 * @property {string | string[]} [deps] - The names of other fields that affect validation of this input.
 */

/**
 * Raw Input component for React Hook Form Modal Validation.
 * We need to add additional props to RawInput component.
 * We also need to remove RawInput from a <FormWrapper> component.
 * Inside your form add const { handleSubmit, formState, register } = useForm();
 * and also parse in the following
 * formState={formState}
 * register={register}
 * Also add onClick={handleSubmit(onSubmit)} to your Button with type = 'submit'
 *
 * @param {object} props - The props object.
 * @param {string} props.value - The value of the input
 * @param {string} props.placeholder - The placeholder of the input
 * @param {string} props.type - The type of the input
 * @param {object} props.inputStyle - The style of the input
 * @param {object} props.iconLeft - The icon to be rendered in the left of the Input.(react-icons)
 * @param {object} props.iconRight - The icon to be rendered in the right of the Input.(react-icons)
 * @param {string} props.name - The name of the input for React Hook Form
 * @param {RegisterOptions} props.registerOptions - The register options for React Hook Form.
 * @param {boolean} props.disabled - The disabled state of the input
 * @param {boolean} props.noMargin - The disabled state of the input
 * @param {string} props.label - The label of the input
 * @param {object} props.labelStyle - The style of the label
 * @param {boolean} props.textArea - The text area state of the input
 * @param {boolean} props.noExtras - Ignore the iconLeft, iconRight, errors and label props
 * @param {object} props.formState - (RawInput only) The form state object from useFormContext
 * @param {object} props.register - (RawInput only) The register function from useFormContext
 * @param {object} props.getValues - The getValues function from useFormContext
 * @param {string} props.initialValue - The initial value of the input
 * @param {number} props.max - max input char
 * @param {boolean} props.shouldInheritBackgroundColor - Whether to inherit the background color of the input
 * @returns {JSX.Element} The Input component we use in ETTX Form Wrapper
 */
export const RawInput = ({
  value,
  placeholder,
  type,
  inputStyle,
  iconLeft,
  iconRight,
  name,
  registerOptions,
  disabled,
  noMargin,
  label,
  labelStyle,
  textArea,
  noExtras,
  formState,
  register,
  getValues,
  initialValue,
  max,
  shouldInheritBackgroundColor,
  ...props
}) => {
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const theme = useTheme();

  const {
    COLOR_ERROR_TEXT,
    COLOR_GREY5,
    COLOR_GREY4,
    COLOR_GREY_TEXT,
    COLOR_BLACK,
  } = theme;

  const { errors, isLoading } = formState;

  const errorMessage = getNestedErrorMessage(name, errors);

  const isPassword = type === 'password';
  let textColor;

  if (disabled) {
    textColor = COLOR_GREY5;
  } else {
    textColor = errorMessage ? COLOR_ERROR_TEXT : COLOR_GREY_TEXT;
  }

  /**
   * Password visibility handler
   */
  const passwordVisibilityHandler = () => {
    setIsPasswordVisible((prevState) => !prevState);
    const input = document.querySelector(`input[name="${name}"]`);
    if (input.type === 'password') {
      input.type = 'text';
    } else {
      input.type = 'password';
    }
  };

  const renderLabel = () => {
    if (!noExtras) {
      if (label) {
        return (
          <Styled.Label errorMessage={errorMessage} style={labelStyle}>
            {label}
          </Styled.Label>
        );
      }
      return <Styled.NoLabel />;
    }
    return null;
  };

  const { setValue } = useFormContext();

  useEffect(() => {
    if (initialValue) {
      setValue(name, initialValue);
    }
  }, [initialValue, name, setValue]);

  return (
    <Styled.Container $noMargin={noMargin}>
      {renderLabel()}
      {textArea ? (
        <Styled.EttxTextArea
          {...register(name, {
            ...registerOptions,
            validate: registerOptions.validate
              ? (val) => registerOptions.validate(val, getValues)
              : undefined,
          })}
          disabled={isLoading || disabled}
          defaultValue={initialValue}
          value={value || undefined}
          placeholder={placeholder}
          $placeholderColor={COLOR_GREY4}
          $iconLeft={iconLeft}
          $iconRight={iconRight}
          style={{
            color: disabled ? COLOR_GREY4 : COLOR_BLACK,
            ...inputStyle,
          }}
          {...props}
        />
      ) : (
        <Styled.InputWrapper
          style={inputStyle}
          disabled={disabled}
          $errorMessage={errorMessage}
        >
          {!noExtras && iconLeft && (
            <Styled.IconContainer style={{ color: textColor }}>
              {iconLeft}
            </Styled.IconContainer>
          )}
          <Styled.EttxInput
            {...register(name, {
              ...registerOptions,
              validate: registerOptions.validate
                ? (val) => registerOptions.validate(val, getValues)
                : undefined,
            })}
            $shouldInheritBackgroundColor={shouldInheritBackgroundColor}
            disabled={isLoading || disabled}
            type={type}
            $noExtras={noExtras}
            value={value || undefined}
            placeholder={placeholder}
            $iconLeft={iconLeft}
            $iconRight={iconRight}
            $errorMessage={errorMessage}
            onWheel={(e) => e.target.blur()} // Prevents scrolling on number inputs
            defaultValue={initialValue}
            maxLength={max}
          />
          {!noExtras && iconRight && !isPassword && (
            <Styled.IconContainer style={{ color: textColor }}>
              {iconRight}
            </Styled.IconContainer>
          )}
          {!noExtras && isPassword && (
            <Styled.IconContainer>
              {isPasswordVisible ? (
                <Styled.EyeIconClose
                  onClick={passwordVisibilityHandler}
                  style={{ color: disabled ? COLOR_GREY4 : COLOR_GREY_TEXT }}
                />
              ) : (
                <Styled.EyeIconOpen
                  onClick={passwordVisibilityHandler}
                  style={{ color: disabled ? COLOR_GREY4 : COLOR_GREY_TEXT }}
                />
              )}
            </Styled.IconContainer>
          )}
        </Styled.InputWrapper>
      )}
      {!noExtras && <ValidationErrorMessage message={errorMessage} />}
    </Styled.Container>
  );
};

const InputPropTypes = {
  /** The value of the input */
  value: propTypes.string,
  /** The placeholder of the input */
  placeholder: propTypes.string,
  /** The type of the input */
  type: propTypes.string,
  /** The style of the input */
  inputStyle: propTypes.objectOf(
    propTypes.oneOfType([propTypes.string, propTypes.number]),
  ),
  /** The style of the error message */
  errorStyle: propTypes.objectOf(propTypes.string),
  /** The icon to be rendered in the left in the Input. (react-icons) */
  iconLeft: propTypes.shape({}),
  /** The icon to be rendered in the right in the Input.(react-icons). This will not work when the type is password */
  iconRight: propTypes.shape({}),
  methods: propTypes.shape({}),
  /** The name of the input for React Hook Form */
  name: propTypes.string.isRequired,
  /** The register options for React Hook Form */
  registerOptions: propTypes.shape({
    validate: propTypes.func,
  }),
  /** The disabled state of the input */
  disabled: propTypes.bool,
  /** The noMargin state of the input */
  noMargin: propTypes.bool,
  /** The noExtras state of the input */
  noExtras: propTypes.bool,
  /** The label of the input */
  label: propTypes.string,
  /** The style of the label */
  labelStyle: propTypes.objectOf(propTypes.string),
  /** The text area state of the input */
  textArea: propTypes.bool,
  required: propTypes.shape({}),
  getValues: propTypes.shape({}),
  /** (RawInput only) The form state object from useForm */
  formState: propTypes.shape({
    errors: propTypes.shape({}),
    isLoading: propTypes.bool,
  }),
  /** (RawInput only) The register function from useForm */
  register: propTypes.func,
  /** The initial value of the input */
  initialValue: propTypes.string,
  max: propTypes.number,
  /** Whether to inherit the background color of the input */
  shouldInheritBackgroundColor: propTypes.bool,
};

const inputDefaultPropTypes = {
  getValues: {},
  value: '',
  label: undefined,
  placeholder: '',
  type: 'text',
  inputStyle: {},
  errorStyle: {},
  labelStyle: {},
  iconLeft: null,
  iconRight: null,
  registerOptions: {
    onChange: () => {},
    required: {},
  },
  textArea: false,
  disabled: false,
  noMargin: false,
  noExtras: false,
  methods: undefined,
  required: {},
  formState: {
    errors: {},
    isLoading: false,
  },
  register: () => {},
  initialValue: '',
  max: 255,
  shouldInheritBackgroundColor: false,
};

RawInput.propTypes = InputPropTypes;

RawInput.defaultProps = inputDefaultPropTypes;

/**
 * ETTX Input component
 * @param {object} props - The props object.
 * @returns {JSX.Element} The Input component we use in ETTX Form Wrapper
 */
const Input = (props) => {
  const { formState, register, getValues } = useFormContext();

  return (
    <RawInput
      {...props}
      register={register}
      getValues={getValues}
      formState={formState}
    />
  );
};

Input.propTypes = InputPropTypes;
Input.defaultProps = inputDefaultPropTypes;

export default Input;
